import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdjustments, useLeaveBalances, useLeaveRequests, useLoans, usePayslips } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { localName, money } from "../../lib/format";
import type { HrMe } from "../../lib/typesHr";
import { DownloadButton } from "../ui/DownloadButton";
import { Empty, SectionHeader } from "../ui/Badges";
import { CorrectionDialog } from "./AttendancePanel";
import { AdjustmentsTable } from "./AdjustmentsPanel";
import { LeaveRequestDialog, LeaveTable } from "./LeavePanel";
import { LoanRequestDialog, LoansTable } from "./LoansPanel";
import { PayslipDialog } from "./PayrollPanel";
import { dateOnly, StatusPill, Table } from "./shared";

/** Self-service: everything about ME — balances, my requests, my payslips, my advances. Row-level security guarantees nothing else can appear here. */
export function MyHr({ me }: { me: HrMe }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const balances = useLeaveBalances();
  const leave = useLeaveRequests({ mine: true });
  const slips = usePayslips({ mine: true });
  const loans = useLoans({ mine: true });
  const adj = useAdjustments({ mine: true });
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [loanOpen, setLoanOpen] = useState(false);
  const [corr, setCorr] = useState(false);
  const [slip, setSlip] = useState<string | null>(null);

  if (!me.employee) return <section className="card p-5"><Empty>{t("hr.my.notLinked")}</Empty></section>;
  const e = me.employee;
  return (
    <div className="space-y-5">
      <section className="card flex flex-wrap items-center justify-between gap-3 p-5">
        <div>
          <div className="text-lg font-black">{locale === "ar" && e.full_name_ar ? e.full_name_ar : e.full_name}</div>
          <div className="text-xs font-semibold text-gray-500"><span className="num font-mono">{e.employee_no}</span> · {(locale === "ar" && e.job_title_ar ? e.job_title_ar : e.job_title) ?? "—"} · {t("hr.hired")} <span className="num">{dateOnly(e.hire_date, locale)}</span></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary" onClick={() => setLeaveOpen(true)}>🌴 {t("hr.leave.newRequest")}</button>
          <button type="button" className="btn-ghost" onClick={() => setLoanOpen(true)}>💳 {t("hr.loans.new")}</button>
          <button type="button" className="btn-ghost" onClick={() => setCorr(true)}>✏️ {t("hr.attendance.correction")}</button>
        </div>
      </section>

      <section className="card space-y-3 p-5" aria-label={t("hr.my.balances")}>
        <SectionHeader icon="⚖️" title={t("hr.my.balances")} />
        <div className="grid gap-3 sm:grid-cols-3">
          {(balances.data ?? []).filter((b) => b.tracks_balance).map((b) => (
            <div key={b.leave_type_id} className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
              <div className="text-[10px] font-black uppercase text-gray-500">{localName({ name: b.name, nameAr: b.name_ar }, locale)}</div>
              <div className="num mt-1 font-mono text-2xl font-black text-cosmic-purple">{b.balance}</div>
              <div className="text-[10px] font-semibold text-gray-500">{t("hr.leave.accrued")} <span className="num">{b.accrued}</span> · {t("hr.leave.taken")} <span className="num">{b.taken}</span></div>
            </div>
          ))}
        </div>
      </section>

      <section className="card space-y-3 p-5" aria-label={t("hr.my.requests")}>
        <SectionHeader icon="📨" title={t("hr.my.requests")} />
        <LeaveTable rows={leave.data ?? []} level={me.level} mine />
      </section>

      <section className="card space-y-3 p-5" aria-label={t("hr.my.payslips")}>
        <SectionHeader icon="🧾" title={t("hr.my.payslips")} subtitle={t("hr.my.payslipsHelp")} />
        <Table head={[t("hr.payroll.period"), t("hr.payslip.gross"), t("hr.payslip.totalDeductions"), t("hr.payslip.net"), t("hr.status.label"), ""]} empty={(slips.data ?? []).length === 0 ? <Empty>{t("hr.my.noPayslips")}</Empty> : undefined}>
          {(slips.data ?? []).map((p) => (
            <tr key={p.id} className="cursor-pointer hover:bg-purple-50/40" onClick={() => setSlip(p.id)}>
              <td className="font-black">{p.period_label}</td><td className="num">{money(p.gross_fils)}</td><td className="num text-red-700">{money(p.total_deductions_fils)}</td><td className="num font-black">{money(p.net_fils)}</td>
              <td><StatusPill status={p.status} /></td>
              <td className="text-end" onClick={(ev) => ev.stopPropagation()}><DownloadButton path={`/hr/payslips/${p.id}/pdf?locale=${locale}`} fallbackName={`payslip-${p.number}.pdf`} className="btn-ghost !px-2 !py-0.5">📄 PDF</DownloadButton></td>
            </tr>
          ))}
        </Table>
      </section>

      {((loans.data ?? []).length > 0 || (adj.data ?? []).length > 0) && (
        <section className="card space-y-3 p-5">
          <SectionHeader icon="💳" title={t("hr.my.loansAdj")} />
          {(loans.data ?? []).length > 0 && <LoansTable rows={loans.data!} level={me.level} />}
          {(adj.data ?? []).length > 0 && <AdjustmentsTable rows={adj.data!} level={0} />}
        </section>
      )}
      <LeaveRequestDialog open={leaveOpen} onClose={() => setLeaveOpen(false)} />
      <LoanRequestDialog open={loanOpen} onClose={() => setLoanOpen(false)} />
      <CorrectionDialog open={corr} onClose={() => setCorr(false)} />
      {slip && <PayslipDialog id={slip} level={0} onClose={() => setSlip(null)} />}
    </div>
  );
}
